import type { ShouldRevalidateFunction } from '@remix-run/react';
import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { querySearchServiceSearch } from '@modules/search';
import { getProductsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-product';
import { DEFAULT_SOURCE_FILTER } from '@modules/search/util/filter-url-helpers';
import { PRODUCTS_PER_PAGE } from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';

export const LOAD_MORE_PRODUCTS_ACTION = '/actions/load-more-products';

/**
 * Maximum products per request to prevent abuse.
 * Each section contains 14 products (1 featured + 8 top grid + 5 bottom).
 */
const MAX_LIMIT = 100;

/**
 * Default number of products to fetch per request.
 * Uses the shared PRODUCTS_PER_PAGE constant (42) to keep pagination aligned.
 */
const DEFAULT_LIMIT = PRODUCTS_PER_PAGE;

/**
 * Regex pattern for valid category links.
 * Only allows /products followed by optional path segments with alphanumeric characters and hyphens.
 */
const CATEGORY_LINK_PATTERN = /^\/products(\/[\w-]+)*$/;

/**
 * API endpoint to fetch additional products for the "View More" functionality.
 *
 * Query Parameters:
 * - categoryLink: The category URL path (must start with /products)
 * - offset: Starting position for pagination (default: 0)
 * - limit: Number of products to fetch (default: 14, max: 100)
 * - filters: Optional JSON-encoded filter parameters
 *
 * Security:
 * - Validates categoryLink format to prevent path traversal
 * - Enforces max limit to prevent resource exhaustion
 * - Returns empty array on validation failure (no error details exposed)
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);

    // Extract and validate parameters
    const categoryLink = url.searchParams.get('categoryLink');
    const offsetParam = url.searchParams.get('offset');
    const limitParam = url.searchParams.get('limit');
    const filtersParam = url.searchParams.get('filters');

    // SECURITY: Validate categoryLink format
    if (!categoryLink || !categoryLink.startsWith('/products')) {
        logger.warning('Invalid categoryLink in load-more-products', {
            categoryLink,
        });
        return json({ products: [], hasMore: false });
    }

    // SECURITY: Prevent path traversal attacks
    if (categoryLink.includes('..')) {
        logger.warning('Path traversal attempt in load-more-products', {
            categoryLink,
        });
        return json({ products: [], hasMore: false });
    }

    // SECURITY: Validate categoryLink matches expected pattern
    if (!CATEGORY_LINK_PATTERN.test(categoryLink)) {
        logger.warning('Invalid categoryLink pattern in load-more-products', {
            categoryLink,
        });
        return json({ products: [], hasMore: false });
    }

    // Parse and validate offset
    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    if (!Number.isInteger(offset) || offset < 0) {
        logger.warning('Invalid offset in load-more-products', { offsetParam });
        return json({ products: [], hasMore: false });
    }

    // Parse and validate limit
    const limit = limitParam ? parseInt(limitParam, 10) : DEFAULT_LIMIT;
    if (!Number.isInteger(limit) || limit < 1 || limit > MAX_LIMIT) {
        logger.warning('Invalid limit in load-more-products', { limitParam });
        return json({ products: [], hasMore: false });
    }

    // Parse optional filters
    let filters: Record<string, unknown> = { categoryLink };
    if (filtersParam) {
        try {
            const parsedFilters = JSON.parse(filtersParam);
            if (typeof parsedFilters === 'object' && parsedFilters !== null) {
                filters = { ...parsedFilters, categoryLink };
            }
        } catch {
            // Ignore invalid JSON, use default filters
            logger.warning('Invalid filters JSON in load-more-products', {
                filtersParam,
            });
        }
    }

    try {
        // Both initial load and View More use PRODUCTS_PER_PAGE products (3 sections),
        // so offset is always a multiple of limit. Math.floor guards against
        // manipulated requests with unaligned offsets.
        const page = Math.floor(offset / limit);

        const searchResults = await querySearchServiceSearch({
            context,
            queryParams: {
                searchType: 'product',
                visibilityType: 'directory',
                filters,
                page,
                size: limit,
                sortBy: 'featured',
                sourceFilter: DEFAULT_SOURCE_FILTER,
            },
        });

        let products = getProductsFromESSearchResults(searchResults, null);

        // Trim to limit (last page may have fewer products)
        if (products.length > limit) {
            products = products.slice(0, limit);
        }

        // Wrap products in TileViewModel format to match loader output
        const productTiles = products.map((product) => ({
            aspectRatio: 'square' as const,
            model: product,
        }));

        // Determine if there are more products to load
        const totalCount = searchResults?.nbHits ?? 0;
        const hasMore = offset + productTiles.length < totalCount;

        return json({
            products: productTiles,
            hasMore,
            totalCount,
        });
    } catch (error) {
        logger.error('Error in /actions/load-more-products', {
            error,
            categoryLink,
            offset,
            limit,
        });
        return json({ products: [], hasMore: false }, { status: 500 });
    }
};

/**
 * Prevent revalidation on navigation - data is fetched on demand.
 */
export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
